import type { RangeNumberLineSpec } from './range-number-line.js';

/**
 * The pH scale, with the companion scale that makes it a scale at all: each
 * whole step is a tenfold change in hydrogen-ion concentration. Blood is the
 * taught mark — its survivable range is 7.35 to 7.45, one seventieth of the
 * axis, and 0.10 of a pH unit is still a 26 % change in [H⁺] (10^0.10 = 1.26).
 */
export function rangeNumberLineFixture(width = 640, focusMarkId = 'blood'): RangeNumberLineSpec {
  return {
    id: 'ph-scale',
    family: 'range-number-line',
    view: 'orthographic',
    viewport: { width, height: width <= 450 ? 470 : 420 },
    subject: 'pH',
    quantity: 'pH',
    domain: { min: 0, max: 14 },
    tickStep: 1,
    bands: [
      { id: 'acidic', label: 'Acidic', from: 0, to: 7 },
      { id: 'alkaline', label: 'Alkaline', from: 7, to: 14 },
    ],
    threshold: { value: 7, label: 'Neutral at 25 °C' },
    marks: [
      { id: 'gastric-acid', label: 'Gastric acid', from: 1.5, to: 3.5, detail: 'fasting stomach contents' },
      { id: 'pure-water', label: 'Pure water', from: 7, detail: 'exactly neutral at 25 °C' },
      { id: 'blood', label: 'Human blood', from: 7.35, to: 7.45, detail: '0.10 pH wide · a 26 % change in [H⁺]' },
      { id: 'seawater', label: 'Ocean surface', from: 8.1, detail: 'about 8.2 before industrialisation' },
    ],
    companion: {
      label: 'Hydrogen ions, mol/L',
      entries: [
        { at: 0, text: '10⁰' },
        { at: 2, text: '10⁻²' },
        { at: 4, text: '10⁻⁴' },
        { at: 6, text: '10⁻⁶' },
        { at: 8, text: '10⁻⁸' },
        { at: 10, text: '10⁻¹⁰' },
        { at: 12, text: '10⁻¹²' },
        { at: 14, text: '10⁻¹⁴' },
      ],
    },
    focusMarkId,
    title: 'One step along this axis is a tenfold change',
    subtitle: 'Position carries the whole claim, so the width a value occupies has to be the width it really has.',
    provenance: 'pH definition at 25 °C; gastric, blood and surface-ocean ranges from standard physiology and oceanography references',
  };
}
